import CalendarMonthOutlined from '@mui/icons-material/CalendarMonthOutlined'
import ChevronRight from '@mui/icons-material/ChevronRight'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCalendarEvents, useCalendars } from '../api/hooks'
import { addDays, fmtTime, startOfDay } from '../lib/dates'
import { useTokens } from '../theme'

/** Right-hand quick panel: today's agenda without leaving the inbox. */
export function SidePanel() {
  const t = useTokens()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const calendars = useCalendars()
  const from = useMemo(() => startOfDay(new Date()), [])
  const events = useCalendarEvents(from, addDays(from, 1), open && !calendars.isError)
  const color = (id: string) => calendars.data?.find((c) => c.id === id)?.color ?? '#005040'
  return (
    <Box sx={{ display: 'flex', flexShrink: 0 }}>
      {open && (
        <Box sx={{ width: 280, bgcolor: 'background.paper', p: 2, overflowY: 'auto' }} aria-label="Today's agenda" role="complementary">
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 600 }}>Today · {new Date().toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}</Typography>
            <Tooltip title="Open calendar"><IconButton size="small" aria-label="Open calendar" onClick={() => navigate('/calendar')}><ChevronRight /></IconButton></Tooltip>
          </Box>
          {calendars.isError ? <Typography variant="body2" color="text.secondary">Calendar isn't available for this session.</Typography>
            : events.isPending ? <Typography variant="body2" color="text.secondary">Loading…</Typography>
            : !events.data?.length ? <Typography variant="body2" color="text.secondary">Nothing scheduled today.</Typography>
            : events.data.map((e) => (
              <Box key={e.occurrenceId} sx={{ display: 'flex', gap: 1, py: 0.75 }}>
                <Box sx={{ width: 4, borderRadius: 2, bgcolor: color(e.calendarId) }} />
                <Box sx={{ minWidth: 0 }}><Typography noWrap sx={{ fontWeight: 500 }}>{e.title || '(no title)'}</Typography><Typography variant="caption" color="text.secondary">{e.allDay ? 'All day' : `${fmtTime(new Date(e.start))} – ${fmtTime(new Date(e.end))}`}</Typography></Box>
              </Box>
            ))}
        </Box>
      )}
      <Box sx={{ width: 48, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, pt: 0.5, bgcolor: t.rail }}>
        <Tooltip title="Calendar" placement="left"><IconButton aria-label="Toggle agenda panel" aria-pressed={open} onClick={() => setOpen((v) => !v)} sx={{ bgcolor: open ? t.selected : 'transparent' }}><CalendarMonthOutlined /></IconButton></Tooltip>
      </Box>
    </Box>
  )
}
